import {HttpErrorResponse} from '@angular/common/http';
import {Component, ViewChild} from '@angular/core';
import {FormBuilder, FormControlStatus, FormGroup, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    IncidentApiService,
    IncidentRequestV2,
    IncidentResponse,
    IncidentType,
    IncidentV2ApiService,
} from '@npck/af-frontend-api';
import {getFormattedDateOrNull, NESTED_TAB, RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService, StepperComponent} from '@npck/libs/ui-kit';
import {isEqual} from 'lodash-es';
import {catchError, debounceTime, distinctUntilChanged, filter, Observable, of, Subscription, throwError} from 'rxjs';
import {createAttachmentsRequestV2} from '../../../components/files/files.component';
import {INCIDENT_ID} from '../../../domain/query-params.keys';
import {Path} from '../../../path';
import {parseFieldExpression, showServerValidationErrors, ValidationErrorItem} from '../../../utils/server.field.validation.utils';
import {isAnyFormFillStarted} from '../../../utils/form.utils';
import {IncidentDetailsTab} from '../details/incident-details.component';
import {IncidentsTab} from '../incidents.component';
import {IncidentAmountForm} from './amount-information-step/amount-information-step.component';
import {CommonInformationForm} from './common-information-step/common-information-step.component';
import {
    ContactDetailsForm,
    createContactDetailsRequestV2,
} from './contact-details-step/contact-details-step.component';
import {createMsisdnFraudEntryRequest, MsisdnFraudEntriesForm} from './msisdn-fraud-step/msisdn-fraud-step.component';
import {TransactionsForm} from './transaction-information-step/transaction-information-step.component';
import {
    createTransactionRequestV2,
    normalizeBlankStringsToUndefined,
} from './transaction-information-step/transcation-information/transaction-information.component';

@Component({
    templateUrl: './create-edit-incident.component.html',
    standalone: false,
})
export class CreateEditIncidentComponent {
    @ViewChild('stepper') stepper: StepperComponent;
    Spinner = Spinner;
    form: FormGroup<IncidentForm>;
    steps: Step[] = ['incident', 'contactDetails', 'amount', 'transaction'];
    currentStep: Step = 'incident';
    lastSteps: Step[] = ['transaction', 'requisites', 'msisdnFraudEntries'];
    hasReachedLastStep: boolean = false;
    incidentId: string;
    incidentResponse: IncidentResponse;
    private initialFormValue: IncidentFormValue;
    private formInitializeSubscription: Subscription;

    private backToTab: IncidentsTab;
    private backToNestedTab: IncidentDetailsTab;
    private isFormChanged: boolean;

    constructor(
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private formBuilder: FormBuilder,
        private incidentApiService: IncidentApiService,
        private incidentV2ApiService: IncidentV2ApiService,
        private snackbarService: SnackbarService,
        private dialogService: DialogService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.paramMap.get(INCIDENT_ID);
        this.backToTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as IncidentsTab;
        this.backToNestedTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as IncidentDetailsTab;
        if (this.incidentId) {
            this.incidentApiService.getIncident(this.incidentId)
                .pipe(rxSpinner('initial'))
                .subscribe(incident => {
                    this.buildForm(incident);
                    this.subscribeToFormInitializations();
                });
        } else {
            this.buildForm();
        }
    }

    get currentStepFormValid(): boolean {
        switch (this.currentStep) {
            case 'incident':
                return this.form.controls.commonInformationForm?.valid ?? true;
            case 'contactDetails':
                return this.form.controls.contactDetailsForm?.valid ?? true;
            case 'amount':
                return this.form.controls.incidentAmountForm?.valid ?? true;
            case 'transaction':
            case 'requisites':
                return this.form.controls.transactionsForm?.valid ?? true;
            case 'msisdnFraudEntries':
                return this.form.controls.msisdnFraudEntriesForm?.valid ?? true;
        }
    }

    isStepCompleted = (stepName: string): boolean => {
        switch (stepName as Step) {
            case 'incident':
                return this.form.controls.commonInformationForm?.valid ?? false;
            case 'contactDetails':
                return this.form.controls.contactDetailsForm?.valid ?? false;
            case 'amount':
                return this.form.controls.incidentAmountForm?.valid ?? false;
            case 'transaction':
            case 'requisites':
                return this.form.controls.transactionsForm?.valid ?? false;
            case 'msisdnFraudEntries':
                return this.form.controls.msisdnFraudEntriesForm?.valid ?? false;
        }
    };

    filterIncidentIdPath = (path: string): boolean => {
        return path === this.incidentId;
    };

    navigateBack = (): Observable<boolean> => {
        if ((!this.incidentId && isAnyFormFillStarted(this.form)) || (this.incidentId && this.isFormChanged)) {
            return this.dialogService.openDialog({
                title: 'i18n_af_dialogCreateIncidentWarningBeforeExitTitle',
                subtitle: 'i18n_af_dialogCreateIncidentWarningBeforeExitSubtitle',
                buttonOkText: 'i18n_af_buttonExit',
                buttonCancelText: 'i18n_af_buttonCancel',
                isButtonOkAccent: true,
            }, {disableClose: true});
        } else {
            return of(true);
        }
    };

    openCancellationWarningDialog(): void {
        this.navigateBack()
            .subscribe(result => {
                if (result) {
                    if (this.incidentId) {
                        this.routerService.navigate(
                            `${Path.INCIDENTS}/${this.incidentId}`,
                            {
                                queryParams: {
                                    [TAB]: this.backToTab,
                                    [NESTED_TAB]: this.backToNestedTab,
                                },
                            },
                        );
                    } else {
                        this.routerService.navigateToTab<IncidentsTab>(Path.INCIDENTS, this.backToTab);
                    }
                }
            });
    }

    onStepChange(index: number): void {
        this.currentStep = this.steps[index];
        if (index === this.steps.length - 1) {
            this.hasReachedLastStep = true;
        }
    }

    sendRequest(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        if (this.incidentId && !this.isFormChanged) {
            return;
        }
        if (this.incidentId) {
            this.updateIncident();
        } else {
            this.createIncident();
        }
    }

    changeStepper(event: string): void {
        if (!event) {
            return;
        }
        this.hasReachedLastStep = false;
        const finalStep = event === IncidentType.COMPROMISED_PAYMENT_INSTRUMENT.toString() ? 'requisites' : 'transaction';
        if (event === IncidentType.MSISDN_FRAUD) {
            this.steps = ['incident', 'msisdnFraudEntries'];
            this.form.removeControl('contactDetailsForm');
            this.form.removeControl('incidentAmountForm');
            this.form.removeControl('transactionsForm');
            if (!this.form.controls.msisdnFraudEntriesForm) {
                this.form.addControl('msisdnFraudEntriesForm', this.formBuilder.group({}));
            }
        } else if (event === IncidentType.OTHER || event === IncidentType.COMPROMISED_PAYMENT_INSTRUMENT) {
            this.steps = ['incident', finalStep];
            this.form.removeControl('contactDetailsForm');
            this.form.removeControl('incidentAmountForm');
            this.form.removeControl('msisdnFraudEntriesForm');
            if (!this.form.controls.transactionsForm) {
                this.form.addControl('transactionsForm', this.formBuilder.group({}));
            }
        } else if (!this.incidentId) {
            this.steps = ['incident', 'contactDetails', 'amount', 'transaction'];
            this.form.removeControl('msisdnFraudEntriesForm');
            if (!this.form.controls.contactDetailsForm) {
                this.form.addControl('contactDetailsForm', this.formBuilder.group({}));
            }
            if (!this.form.controls.incidentAmountForm) {
                this.form.addControl('incidentAmountForm', this.formBuilder.group({}));
            }
            if (!this.form.controls.transactionsForm) {
                this.form.addControl('transactionsForm', this.formBuilder.group({}));
            }
        } else {
            this.steps = ['incident', 'amount', finalStep];
            this.form.removeControl('msisdnFraudEntriesForm');
            if (!this.form.controls.incidentAmountForm) {
                this.form.addControl('incidentAmountForm', this.formBuilder.group({}));
            }
            if (!this.form.controls.transactionsForm) {
                this.form.addControl('transactionsForm', this.formBuilder.group({}));
            }
        }
    }

    private subscribeToFormInitializations(): void {
        this.formInitializeSubscription = this.form.statusChanges.pipe(
            rxSpinner('initForms'),
            debounceTime(500),
            distinctUntilChanged(),
            filter((status: FormControlStatus) => status !== 'PENDING'),
        ).subscribe(() => {
            this.formInitializeSubscription.unsubscribe();
            this.initialFormValue = this.form.getRawValue();
            this.subscribeToFormChanges();
        });
    }

    private createIncident(): void {
        this.incidentV2ApiService.createIncident(this.createIncidentRequestV2())
            .pipe(
                rxSpinner('createIncident'),
                catchError(
                    (error: HttpErrorResponse): Observable<any> => {
                        const responseBody = error.error;
                        const validationErrors: ValidationErrorItem[] = responseBody?.errors;
                        if (error.status === 400 && validationErrors?.length) {
                            const serverValidationErrors: ValidationErrorItem[] = [];
                            validationErrors.forEach((item, index, object) => {
                                if (item.code === 'PARTICIPANT_IDENTIFICATION_NUMBER_INVALID') {
                                    serverValidationErrors.push(item);
                                    delete validationErrors[index];
                                }
                            });
                            showServerValidationErrors(serverValidationErrors, (item: ValidationErrorItem) => {
                                const expression = parseFieldExpression(item?.field, 'transactions');

                                switch (expression?.field) {
                                    case 'sender.identificationNumber':
                                        return this.form?.controls?.transactionsForm
                                            ?.controls?.addedTransactions?.controls[expression.index]?.controls?.sender?.controls?.iin;
                                    case 'receiver.identificationNumber':
                                        return this.form?.controls?.transactionsForm
                                            ?.controls?.addedTransactions?.controls[expression.index]?.controls?.receiver?.controls?.iin;
                                    default: return null;
                                }
                            });
                        }
                        return throwError(() => error);
                    },
                ),
            )
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageIncidentCreated');
                this.routerService.navigateToTab<IncidentsTab>(Path.INCIDENTS, this.backToTab);
            });
    }

    private updateIncident(): void {
        this.incidentV2ApiService.updateIncident(this.incidentId, this.createIncidentRequestV2())
            .pipe(rxSpinner('editIncident'))
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageIncidentUpdated');
                this.routerService.navigate(
                    `${Path.INCIDENTS}/${this.incidentId}`,
                    {
                        queryParams: {
                            [TAB]: this.backToTab,
                            [NESTED_TAB]: this.backToNestedTab,
                        },
                    },
                );
            });
    }

    private createIncidentRequestV2(): IncidentRequestV2 {
        const common = this.form.getRawValue().commonInformationForm;
        const amounts = this.form.getRawValue().incidentAmountForm;
        const transactions = [
            ...(this.form.getRawValue().transactionsForm?.transactions ?? []),
            ...(this.form.getRawValue().transactionsForm?.addedTransactions ?? []),
        ];
        const transactionsFormArray = [
            ...(this.form.controls.transactionsForm?.controls?.transactions?.controls ?? []),
            ...(this.form.controls.transactionsForm?.controls?.addedTransactions?.controls ?? []),
            ...(this.form.controls.transactionsForm?.controls?.deletedTransactions?.controls ?? []),
        ]
            .filter(transactionForm => !!Object.keys(transactionForm.getRawValue()).length);
        const contactDetailsFormArray = this.form.controls.contactDetailsForm?.controls?.contacts?.controls ?? [];
        const msisdnFraudEntriesFormArray = this.form.controls.msisdnFraudEntriesForm?.controls?.entries?.controls ?? [];
        const relatedOrganizations = [];
        if (common.relatedOrganizations?.length) {
            relatedOrganizations.push(...common.relatedOrganizations);
        }
        const totalAmount = [amounts?.lossCreditAmount, amounts?.lossOwnAmount]
            .map(value => Number(value) || 0)
            .reduce((sum, value) => sum + value);
        const lastTransaction = transactions[transactions.length - 1];
        return normalizeBlankStringsToUndefined(<IncidentRequestV2>{
            currencyAmountLossCredit: amounts?.lossCreditAmountCurrency,
            currencyAmountLossOwn: amounts?.lossOwnAmountCurrency,
            currencyAmountSaveCredit: amounts?.saveCreditAmountCurrency,
            currencyAmountSaveOwn: amounts?.saveOwnAmountCurrency,
            amountLossCredit: amounts?.lossCreditAmount ? Number(amounts.lossCreditAmount) : (this.incidentId ? 0 : undefined),
            amountLossOwn: amounts?.lossOwnAmount ? Number(amounts.lossOwnAmount) : (this.incidentId ? 0 : undefined),
            amountSaveCredit: amounts?.saveCreditAmount ? Number(amounts.saveCreditAmount) : (this.incidentId ? 0 : undefined),
            amountSaveOwn: amounts?.saveOwnAmount ? Number(amounts.saveOwnAmount) : (this.incidentId ? 0 : undefined),
            type: common.type,
            subtype: common.fraudType,
            description: common.description,
            currency: this.incidentId || !lastTransaction ? undefined : lastTransaction.currency,
            date: this.incidentId || !lastTransaction ? undefined : getFormattedDateOrNull(lastTransaction.transactionDate?.date),
            basedOn: common.basedOn,
            amount: totalAmount || undefined,
            relatedOrganizations: relatedOrganizations,
            governmentBin: common.governmentOrganizations,
            attachments: createAttachmentsRequestV2(this.form.controls.commonInformationForm.controls.files.value),
            transactions: transactionsFormArray.map(transaction => createTransactionRequestV2(transaction)),
            contactDetails: contactDetailsFormArray.map(contact => createContactDetailsRequestV2(contact)),
            msisdnFraudEntries: msisdnFraudEntriesFormArray.length
                ? msisdnFraudEntriesFormArray.map(entry => createMsisdnFraudEntryRequest(entry))
                : undefined,
        });
    }

    private buildForm(incident?: IncidentResponse): void {
        this.form = this.formBuilder.group({});
        this.form.addControl('commonInformationForm', this.formBuilder.group({}));
        if (incident) {
            if (incident.type !== 'Иное') {
                this.form.addControl('incidentAmountForm', this.formBuilder.group({}));
            } else {
                this.steps = ['incident', 'transaction'];
            }
        } else {
            this.form.addControl('contactDetailsForm', this.formBuilder.group({}));
            this.form.addControl('incidentAmountForm', this.formBuilder.group({}));
        }
        this.form.addControl('transactionsForm', this.formBuilder.group({}));
        this.incidentResponse = incident;
        Spinner.hide('initial');
    }

    private subscribeToFormChanges(): void {
        this.form.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
        ).subscribe(value => {
            this.isFormChanged = !isEqual(this.initialFormValue, value);
        });
    }

}

declare type IncidentFormValue = ɵTypedOrUntyped<IncidentForm, ɵFormGroupRawValue<IncidentForm>, any>;

export interface IncidentForm {
    commonInformationForm?: FormGroup<CommonInformationForm>;
    contactDetailsForm?: FormGroup<ContactDetailsForm>;
    incidentAmountForm?: FormGroup<IncidentAmountForm>;
    transactionsForm?: FormGroup<TransactionsForm>;
    msisdnFraudEntriesForm?: FormGroup<MsisdnFraudEntriesForm>;
}

declare type Step = 'incident' | 'contactDetails' | 'amount' | 'transaction' | 'requisites' | 'msisdnFraudEntries';

export function sanitizeDigits(value?: string): string | null {
    if (!value) {
        return null;
    }
    const digits = value.replace(/\D+/g, '').trim();
    return digits.length > 0 ? digits : null;
}
